package com.evoapps.evoshift.ui.util

import evoshift.composeapp.generated.resources.month_sentence_lowercase
import com.evoapps.evoshift.domain.util.AppClock
import androidx.compose.runtime.Composable
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.date_day_month
import evoshift.composeapp.generated.resources.day_monday_in_sentence
import evoshift.composeapp.generated.resources.day_on_monday
import evoshift.composeapp.generated.resources.day_tuesday_in_sentence
import evoshift.composeapp.generated.resources.day_on_tuesday
import evoshift.composeapp.generated.resources.day_wednesday_in_sentence
import evoshift.composeapp.generated.resources.day_on_wednesday
import evoshift.composeapp.generated.resources.day_thursday_in_sentence
import evoshift.composeapp.generated.resources.day_on_thursday
import evoshift.composeapp.generated.resources.day_friday_in_sentence
import evoshift.composeapp.generated.resources.day_on_friday
import evoshift.composeapp.generated.resources.day_saturday_in_sentence
import evoshift.composeapp.generated.resources.day_on_saturday
import evoshift.composeapp.generated.resources.day_sunday_in_sentence
import evoshift.composeapp.generated.resources.day_on_sunday
import evoshift.composeapp.generated.resources.day_fri_short
import evoshift.composeapp.generated.resources.day_friday
import evoshift.composeapp.generated.resources.day_mon_short
import evoshift.composeapp.generated.resources.day_monday
import evoshift.composeapp.generated.resources.day_sat_short
import evoshift.composeapp.generated.resources.day_saturday
import evoshift.composeapp.generated.resources.day_sun_short
import evoshift.composeapp.generated.resources.day_sunday
import evoshift.composeapp.generated.resources.day_thu_short
import evoshift.composeapp.generated.resources.day_thursday
import evoshift.composeapp.generated.resources.day_tue_short
import evoshift.composeapp.generated.resources.day_tuesday
import evoshift.composeapp.generated.resources.day_wed_short
import evoshift.composeapp.generated.resources.day_wednesday
import evoshift.composeapp.generated.resources.month_in_1
import evoshift.composeapp.generated.resources.month_in_2
import evoshift.composeapp.generated.resources.month_in_3
import evoshift.composeapp.generated.resources.month_in_4
import evoshift.composeapp.generated.resources.month_in_5
import evoshift.composeapp.generated.resources.month_in_6
import evoshift.composeapp.generated.resources.month_in_7
import evoshift.composeapp.generated.resources.month_in_8
import evoshift.composeapp.generated.resources.month_in_9
import evoshift.composeapp.generated.resources.month_in_10
import evoshift.composeapp.generated.resources.month_in_11
import evoshift.composeapp.generated.resources.month_in_12
import evoshift.composeapp.generated.resources.month_1
import evoshift.composeapp.generated.resources.month_10
import evoshift.composeapp.generated.resources.month_11
import evoshift.composeapp.generated.resources.month_12
import evoshift.composeapp.generated.resources.month_2
import evoshift.composeapp.generated.resources.month_3
import evoshift.composeapp.generated.resources.month_4
import evoshift.composeapp.generated.resources.month_5
import evoshift.composeapp.generated.resources.month_6
import evoshift.composeapp.generated.resources.month_7
import evoshift.composeapp.generated.resources.month_8
import evoshift.composeapp.generated.resources.month_9
import evoshift.composeapp.generated.resources.month_gen_1
import evoshift.composeapp.generated.resources.month_gen_10
import evoshift.composeapp.generated.resources.month_gen_11
import evoshift.composeapp.generated.resources.month_gen_12
import evoshift.composeapp.generated.resources.month_gen_2
import evoshift.composeapp.generated.resources.month_gen_3
import evoshift.composeapp.generated.resources.month_gen_4
import evoshift.composeapp.generated.resources.month_gen_5
import evoshift.composeapp.generated.resources.month_gen_6
import evoshift.composeapp.generated.resources.month_gen_7
import evoshift.composeapp.generated.resources.month_gen_8
import evoshift.composeapp.generated.resources.month_gen_9
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import org.jetbrains.compose.resources.StringResource
import androidx.compose.runtime.remember
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource

/**
 * Day and month names in the UI language. Never print `DayOfWeek.name` / `Month.name`: those are
 * English enum constants ("TUESDAY", "FEBRUARY").
 */
internal val months = listOf(
    Res.string.month_1, Res.string.month_2, Res.string.month_3, Res.string.month_4, Res.string.month_5, Res.string.month_6,
    Res.string.month_7, Res.string.month_8, Res.string.month_9, Res.string.month_10, Res.string.month_11, Res.string.month_12
)

/** Month in a date ("3 lutego" in Polish needs the genitive; English is the same as [monthName]). */
private val monthsInDate = listOf(
    Res.string.month_gen_1, Res.string.month_gen_2, Res.string.month_gen_3, Res.string.month_gen_4, Res.string.month_gen_5, Res.string.month_gen_6,
    Res.string.month_gen_7, Res.string.month_gen_8, Res.string.month_gen_9, Res.string.month_gen_10, Res.string.month_gen_11, Res.string.month_gen_12
)

private fun DayOfWeek.shortName(): StringResource = when (this) {
    DayOfWeek.MONDAY -> Res.string.day_mon_short
    DayOfWeek.TUESDAY -> Res.string.day_tue_short
    DayOfWeek.WEDNESDAY -> Res.string.day_wed_short
    DayOfWeek.THURSDAY -> Res.string.day_thu_short
    DayOfWeek.FRIDAY -> Res.string.day_fri_short
    DayOfWeek.SATURDAY -> Res.string.day_sat_short
    else -> Res.string.day_sun_short
}

private fun DayOfWeek.fullName(): StringResource = when (this) {
    DayOfWeek.MONDAY -> Res.string.day_monday
    DayOfWeek.TUESDAY -> Res.string.day_tuesday
    DayOfWeek.WEDNESDAY -> Res.string.day_wednesday
    DayOfWeek.THURSDAY -> Res.string.day_thursday
    DayOfWeek.FRIDAY -> Res.string.day_friday
    DayOfWeek.SATURDAY -> Res.string.day_saturday
    else -> Res.string.day_sunday
}

private fun DayOfWeek.inSentenceName(): StringResource = when (this) {
    DayOfWeek.MONDAY -> Res.string.day_monday_in_sentence
    DayOfWeek.TUESDAY -> Res.string.day_tuesday_in_sentence
    DayOfWeek.WEDNESDAY -> Res.string.day_wednesday_in_sentence
    DayOfWeek.THURSDAY -> Res.string.day_thursday_in_sentence
    DayOfWeek.FRIDAY -> Res.string.day_friday_in_sentence
    DayOfWeek.SATURDAY -> Res.string.day_saturday_in_sentence
    else -> Res.string.day_sunday_in_sentence
}

private fun DayOfWeek.onName(): StringResource = when (this) {
    DayOfWeek.MONDAY -> Res.string.day_on_monday
    DayOfWeek.TUESDAY -> Res.string.day_on_tuesday
    DayOfWeek.WEDNESDAY -> Res.string.day_on_wednesday
    DayOfWeek.THURSDAY -> Res.string.day_on_thursday
    DayOfWeek.FRIDAY -> Res.string.day_on_friday
    DayOfWeek.SATURDAY -> Res.string.day_on_saturday
    else -> Res.string.day_on_sunday
}

/** "Luty" / "February" for month 1..12. */
@Composable
fun monthName(month: Int): String = stringResource(months[month - 1])

/** SH-165: "w październiku" / "in October" (Polish and Ukrainian need the locative). */
private val monthsAfterIn = listOf(
    Res.string.month_in_1, Res.string.month_in_2, Res.string.month_in_3, Res.string.month_in_4, Res.string.month_in_5, Res.string.month_in_6,
    Res.string.month_in_7, Res.string.month_in_8, Res.string.month_in_9, Res.string.month_in_10, Res.string.month_in_11, Res.string.month_in_12
)

/** The resource of [inMonth] (tests word the screens from it). */
fun inMonthRes(month: Int): StringResource = monthsAfterIn[month - 1]

/**
 * "w październiku" / "we wrześniu" / "in October" / "у жовтні": when, with its preposition (Polish
 * needs "we" before "wrześniu"), for titles like "Moje szacunkowe wynagrodzenie w październiku".
 */
@Composable
fun inMonth(month: Int): String = stringResource(monthsAfterIn[month - 1])

/** SH-167: "listopad" as a word inside a sentence: lower case where the language writes it so (Polish, Ukrainian). */
@Composable
fun monthInSentence(month: Int): String {
    val name = monthName(month)
    return if (stringResource(Res.string.month_sentence_lowercase) == "true") name.lowercase() else name
}

/** The current month in the device's time zone, 1..12. */
@Composable
fun currentMonthNumber(): Int = remember { AppClock.now().toLocalDateTime(TimeZone.currentSystemDefault()).month.number }

/** "Pon" / "Mon". */
@Composable
fun dayShort(day: DayOfWeek): String = stringResource(day.shortName())

/** "Poniedziałek" / "Monday". */
@Composable
fun dayFull(day: DayOfWeek): String = stringResource(day.fullName())

/** The resource of [dayFull] (tests word the screens from it). */
fun dayFullRes(day: DayOfWeek): StringResource = day.fullName()

/**
 * "Wtorek, 3 lutego" / "Tuesday, 3 February": a heading or the start of a line. Inside a sentence use
 * [dayAndDateInSentence] ("Termin: wtorek, 3 lutego") or [onDayAndDate] ("kończy się we wtorek, 3 lutego").
 */
@Composable
fun dayAndDate(date: LocalDate): String =
    stringResource(Res.string.date_day_month, dayFull(date.dayOfWeek), date.day, stringResource(monthsInDate[date.month.number - 1]))

/** SH-150: the resource of the month in a date ("lutego" / "February"), for [UiText]. */
fun monthInDateRes(month: Int): StringResource = monthsInDate[month - 1]

/** "3 lutego" / "3 February": a date inside a sentence ("answers by 3 February"). */
@Composable
fun dayAndMonth(date: LocalDate): String = "${date.day} ${stringResource(monthsInDate[date.month.number - 1])}"

/** Name of the current month in the device's time zone. */
@Composable
fun currentMonthName(): String {
    val month = remember { AppClock.now().toLocalDateTime(TimeZone.currentSystemDefault()).month.number }
    return monthName(month)
}

/** "wtorek" / "Tuesday": the weekday inside a sentence (Polish and Ukrainian write it in lower case). */
@Composable
fun dayInSentence(day: DayOfWeek): String = stringResource(day.inSentenceName())

/** The resource of [dayInSentence], for [UiText]. */
fun dayInSentenceRes(day: DayOfWeek): StringResource = day.inSentenceName()

/** "wtorek, 3 lutego" / "Tuesday, 3 February": a date with its weekday inside a sentence. */
@Composable
fun dayAndDateInSentence(date: LocalDate): String =
    stringResource(Res.string.date_day_month, dayInSentence(date.dayOfWeek), date.day, stringResource(monthsInDate[date.month.number - 1]))

/** "we wtorek, 3 lutego" / "on Tuesday, 3 February" / "у вівторок, 3 лютого": when something happens. */
@Composable
fun onDayAndDate(date: LocalDate): String =
    stringResource(Res.string.date_day_month, stringResource(date.dayOfWeek.onName()), date.day, stringResource(monthsInDate[date.month.number - 1]))
